/**
 * Reports > Unposted Activity (/reports/unposted-activity).
 *
 * What CounterPoint is still holding back from posting, in two tables: register
 * drawers opened before today that nobody has posted, and unposted inventory,
 * purchasing and transfer documents. Replaces the QuickSight dashboards
 * "Unposted Activity Report" and "Unposted Inventory Analysis".
 *
 * This page is deliberately thin. The screen itself is the shared
 * `UnpostedActivity` component in `@ampersandbrands/ui` (presentational, so The
 * Sweet Spot can reuse it); this file fetches through `useUnpostedActivity`,
 * keeps the filters in the URL so a filtered view is a shareable link, and adds
 * the page chrome: header with the pin toggle, the daily email control, and the
 * Excel download.
 *
 * The QuickSight view (seven transaction types, quantity zero or below) is one
 * click away through "QuickSight view", so the numbers can be matched against the
 * old dashboard day for day.
 *
 * RBAC: `reports.unposted` sees the whole fleet. A store manager (holding
 * `stores.sweetspot`) reaches an own-store view, enforced server-side like OMNI
 * Performance, so the store picker is hidden when the api reports store scope.
 *
 * Resilience: wrapped in an ErrorBoundary and degrades to a quiet "not available
 * yet" card on 404/503 while the api ships.
 */

import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { UnpostedActivity as UnpostedActivityScreen, type UnpostedActivityFilters } from '@ampersandbrands/ui';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ReportSubscribeControl, reportToolbarBtnCls } from '@/components/ReportSubscribeControl';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { useSession } from '@/lib/useSession';
import { useRetailStores } from '@/lib/useRetailStores';
import { useReportPageState } from '@/lib/reportPageState';
import { isNotAvailable } from '@/lib/useStores';
import { UNPOSTED_REPORT_TYPE } from '@/lib/useReportSubscriptions';
import {
  QUICKSIGHT_UNPOSTED_TYPES,
  downloadUnpostedXlsx,
  parseUnpostedFilters,
  unpostedSubscriptionFilters,
  useUnpostedActivity,
  writeUnpostedFilters,
} from '@/lib/useUnpostedActivity';
import { hasAccessSlug } from '@/lib/toolVisibility';
import { AccessDeniedCard, ErrorCard, NotAvailableCard, StoresHeader } from '../stores/storesShared';

/** The accessSlug that grants this report (fleet view). */
const SLUG = 'reports.unposted';

/**
 * Fleet through `reports.unposted`, or an own-store view through
 * `stores.sweetspot` (the same dual audience as OMNI Performance). The api
 * enforces the real scope; this only decides whether to show the page.
 */
function canViewUnposted(perms: string[] | undefined, superAdmin = false): boolean {
  if (hasAccessSlug(perms, SLUG, superAdmin)) return true;
  return !!perms?.includes('stores.sweetspot');
}

/** True when the filters are exactly the QuickSight view. */
function isQuickSightView(f: UnpostedActivityFilters): boolean {
  return (
    f.nonpositive_only &&
    f.type.length === QUICKSIGHT_UNPOSTED_TYPES.length &&
    QUICKSIGHT_UNPOSTED_TYPES.every((t) => f.type.includes(t))
  );
}

function UnpostedActivityInner() {
  const { user } = useSession();
  const canView = canViewUnposted(user?.permissions, user?.superAdmin);

  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseUnpostedFilters(searchParams), [searchParams]);
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const setFilters = useCallback(
    (next: UnpostedActivityFilters) =>
      setSearchParams(
        (prev) => {
          const sp = new URLSearchParams(prev);
          writeUnpostedFilters(sp, next);
          return sp;
        },
        { replace: true },
      ),
    [setSearchParams],
  );

  const dataQ = useUnpostedActivity(filters, canView);
  const storesQ = useRetailStores();
  // A store-scoped caller sees one store, so the picker would only mislead.
  const storeOptions = useMemo(
    () =>
      dataQ.scope === 'store'
        ? []
        : [...storesQ.stores]
            .sort((a, b) => Number(a.store_no) - Number(b.store_no))
            .map((s) => ({ store_no: String(s.store_no), store_name: s.store_name ?? '' })),
    [storesQ.stores, dataQ.scope],
  );

  const pageState = useMemo(
    () => [
      { label: 'Stores', value: filters.store_no.length > 0 ? filters.store_no.join(', ') : 'All stores' },
      { label: 'Types', value: filters.type.length > 0 ? filters.type.join(', ') : 'All types' },
      ...(filters.nonpositive_only ? [{ label: 'Zero or negative quantity only', value: 'Yes' }] : []),
    ],
    [filters],
  );
  useReportPageState(pageState);

  async function handleExport() {
    setExporting(true);
    setToast(null);
    try {
      await downloadUnpostedXlsx(filters);
    } catch (e) {
      setToast(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setExporting(false);
    }
  }

  if (user && !canView) return <AccessDeniedCard what="Unposted Activity" />;

  const header = (
    <StoresHeader
      eyebrow="Reports"
      title="Unposted Activity"
      subtitle="What CounterPoint is still holding back from posting: register drawers opened before today, and unposted inventory, purchasing, and transfer documents. Shows CounterPoint as of its last overnight copy, so today's postings usually clear the next morning."
      actions={<ToolPinToggle toolSlug="unposted-activity" toolName="Unposted Activity" />}
    />
  );

  if (isNotAvailable(dataQ.error)) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        {header}
        <NotAvailableCard what="Unposted Activity" />
      </div>
    );
  }

  const quickSight = isQuickSightView(filters);

  return (
    <div className="flex h-full min-h-0 flex-col">
      {header}
      {toast && (
        <p role="alert" className="mb-3 text-sm text-red-700 dark:text-red-300">
          {toast}
        </p>
      )}
      <UnpostedActivityScreen
        drawers={dataQ.drawers}
        activity={dataQ.activity}
        types={dataQ.types}
        asOf={dataQ.asOf}
        loading={dataQ.isLoading}
        error={dataQ.error ? <ErrorCard message={dataQ.error.message} /> : undefined}
        filters={filters}
        onFiltersChange={setFilters}
        storeOptions={storeOptions}
        onExport={() => void handleExport()}
        exporting={exporting}
        toolbarExtra={
          <>
            <button
              type="button"
              onClick={() =>
                setFilters(
                  quickSight
                    ? { ...filters, type: [], nonpositive_only: false }
                    : { ...filters, type: [...QUICKSIGHT_UNPOSTED_TYPES], nonpositive_only: true },
                )
              }
              aria-pressed={quickSight}
              title="The old QuickSight view: Inventory Adjustment, Item Merge, Physical Count, Purchase Request, Quick Transfer, Receiving, and Unsettled Credit Cards, with a quantity of zero or below."
              className={reportToolbarBtnCls}
            >
              QuickSight view
            </button>
            <ReportSubscribeControl
              reportType={UNPOSTED_REPORT_TYPE}
              filters={unpostedSubscriptionFilters(filters)}
              subscribeHint="Get this report by email every morning, saving the store, type, and quantity filters you have applied."
            />
          </>
        }
      />
    </div>
  );
}

export function UnpostedActivity() {
  return (
    <ErrorBoundary>
      <UnpostedActivityInner />
    </ErrorBoundary>
  );
}
